import { type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { FloatingShapes } from '@/components/landing/floating-shapes';
import { CenteredGlassCard } from '@/components/guided/centered-glass-card';

type CardMode = 'md' | 'wide' | 'none';

/**
 * 引导式外壳:绚烂动态背景(渐变 + FloatingShapes)+ 居中毛玻璃卡(仅"卡适配页"用)。
 *
 * 卡片持久:GuidedShell 跨路由存活,导航只换卡内 Outlet 内容 → 卡片 height + max-width
 * 随内容/尺寸平滑变形(真正的跨页尺寸 morph),顶栏常驻。
 *
 * 但并非所有路由都该进卡——详情页(/dashboard/competitions/:teamId)、打分页等是全宽密集页,
 * 塞进窄卡会挤坏。故用显式卡路径白名单:白名单内渲染卡(md/wide),其余渲染全宽无卡(零回归)。
 *
 * 尺寸:工具/表单页(messages/preparation/judge-cert/judge-reg)用 wide(max-w-5xl),余卡页用 md。
 * 新增卡适配页需加入 CARD_PATHS;非卡页默认全宽无卡。
 */
const WIDE_PATHS = [
  '/dashboard/messages',
  '/dashboard/preparation',
  '/dashboard/judge-certification',
  '/dashboard/judge-registration',
  '/dashboard/judge',
  '/dashboard/judge/certificates',
];
// 卡适配页(精确匹配)。动态详情/报名页在 useCardMode 里用正则单独处理。
const CARD_PATHS = [
  '/dashboard',
  '/dashboard/profiles',
  '/dashboard/events',
  '/dashboard/competitions',
  '/dashboard/messages',
  '/dashboard/preparation',
  '/dashboard/judge-certification',
  '/dashboard/judge-registration',
  '/dashboard/judge',
  '/dashboard/judge/certificates',
];

function useCardMode(pathname: string): CardMode {
  // competition 级报名卡:/dashboard/enroll/:competitionId
  if (pathname.startsWith('/dashboard/enroll/')) return 'md';
  // event 级报名向导(多步):/dashboard/events/:id/enroll
  if (/^\/dashboard\/events\/[^/]+\/enroll\/?$/.test(pathname)) return 'wide';
  // 赛项/团队详情(内容页:团队/提交/奖项/座位 卡片栈):/dashboard/competitions/:teamId
  if (/^\/dashboard\/competitions\/[^/]+/.test(pathname)) return 'wide';
  // 备赛详情(与父页 /preparation 一致):/dashboard/preparation/:eventId
  if (/^\/dashboard\/preparation\/[^/]+/.test(pathname)) return 'wide';
  if (CARD_PATHS.includes(pathname)) {
    return WIDE_PATHS.includes(pathname) ? 'wide' : 'md';
  }
  return 'none';
}

function GuidedBackground({ children }: { children: ReactNode }) {
  return (
    <div className="relative min-h-screen bg-background">
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-accent/15 to-primary/5 dark:from-primary/15 dark:via-accent/10 dark:to-slate-900" />
        <FloatingShapes />
      </div>
      <div className="relative z-10">{children}</div>
    </div>
  );
}

export function GuidedShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const mode = useCardMode(pathname);
  if (mode === 'none') {
    // 全宽密集页(详情/打分等):不加卡,与重构前一致
    return <GuidedBackground>{children}</GuidedBackground>;
  }
  return (
    <GuidedBackground>
      <CenteredGlassCard size={mode}>{children}</CenteredGlassCard>
    </GuidedBackground>
  );
}
